/**
 * ONE table button's feature: the same settings as a Button widget (the embedded
 * app-design-button-settings - download / API / filter / navigate / popup incl. a
 * popup screen / approval / export), with the option lists worked out from the
 * design. Shared by an action column's cellButtons and its per-row-value buttons.
 */
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';

import { buttonConfigOf, opensPopupScreen } from '../../../../global-components/dvm-action-button/dvm-action-button.types';
import type { LabelValueOption } from '../../../shared/self-service-config.types';
import { readJson } from '../../../shared/self-service-storage.utils';
import { DesignCanvasStore } from '../../state/design-canvas.store';
import { DesignDocumentStore } from '../../state/design-document.store';
import { roundGeometry } from '../../utils/design-geometry.utils';
import { createScreenFrame } from '../../utils/design-node.utils';
import { screensOf } from '../../utils/design-publish.utils';
import { chartTargets, tileDataset, tileFromNode, widgetNodes } from '../../data/design-tiles.utils';
import { APPROVAL_TABLE_TYPES, WIDGET_PALETTE_META } from '../../design-builder.constants';
import { ButtonSettingsComponent } from './button-settings.component';
import { valueSourceOptionsOf } from '../../utils/design-save-request.utils';

/** The option lists a Button's feature settings pick from (see the inspector). */
export interface CellButtonOptions {
  options: Record<string, unknown>;
  targetOptions: LabelValueOption[];
  widgetOptions: LabelValueOption[];
  popupOptions: LabelValueOption[];
  screenOptions: LabelValueOption[];
  sliderOptions: LabelValueOption[];
  approvalTableOptions: LabelValueOption[];
  widgetDatasets: Record<string, string>;
  dashboardOptions: LabelValueOption[];
  /** Input boxes, dropdowns, sliders - "Save changes" columns read them. */
  valueSourceOptions?: LabelValueOption[];
}

export const EMPTY_CELL_BUTTON_OPTIONS: CellButtonOptions = {
  options: {}, targetOptions: [], widgetOptions: [], popupOptions: [], screenOptions: [],
  sliderOptions: [], approvalTableOptions: [], widgetDatasets: {}, dashboardOptions: [], valueSourceOptions: [],
};

@Component({
  selector: 'app-design-cell-button-feature',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ButtonSettingsComponent],
  template: `
<app-design-button-settings
  [hideStyle]="true"
  [appearance]="config()"
  [options]="ctx().options"
  [targetOptions]="ctx().targetOptions"
  [widgetOptions]="ctx().widgetOptions"
  [popupOptions]="ctx().popupOptions"
  [screenOptions]="ctx().screenOptions"
  [sliderOptions]="ctx().sliderOptions"
  [approvalTableOptions]="ctx().approvalTableOptions"
  [widgetDatasets]="ctx().widgetDatasets"
  [dashboardOptions]="ctx().dashboardOptions"
  [valueSourceOptions]="ctx().valueSourceOptions ?? []"
  [popupScreenState]="popupState()"
  (appearanceChange)="configChange.emit($event)"
  (createPopupScreen)="createPopupScreen()"
  (openPopupScreen)="openPopupScreen()"
  (hidePopupScreen)="hidePopupScreen()"
></app-design-button-settings>
<p class="cbf-hint">
  The clicked row is the context: <code>{{ '{row}' }}</code> (the whole row) and each of its columns as
  <code>{{ '{column}' }}</code> (e.g. <code>{{ '{uuid}' }}</code>) work in API bodies, download bodies and navigate targets.
  Popups: use <b>Popup → Screen</b> to design the popup like any screen.
</p>
`,
  styles: [`
    :host { display: block; }
    .cbf-hint { margin: 6px 0 0; color: #667085; line-height: 1.45; font-size: 12px; }
  `],
})
export class CellButtonFeatureComponent {
  /** The Button widget feature settings (ButtonConfig keys). */
  readonly config = input<Record<string, unknown>>({});
  /** The button's label - names its popup screen. */
  readonly label = input('');
  /** The table's name - popup screens are named after it. */
  readonly tableName = input('');
  /** The option lists (the inspector passes them); absent = worked out from the design here. */
  readonly context = input<CellButtonOptions | null>(null);
  /** The feature keys that changed (merge them into the config). */
  readonly configChange = output<Record<string, unknown>>();

  /** Absent outside the Design Builder (e.g. the Layout Builder): no popup screens there. */
  private readonly doc = inject(DesignDocumentStore, { optional: true });
  private readonly canvasView = inject(DesignCanvasStore, { optional: true });

  /** The option lists: as passed in, else from the design document (any other widget, screen, dashboard). */
  readonly ctx = computed<CellButtonOptions>(() => {
    const given = this.context();
    if (given) return given;
    const doc = this.doc;
    if (!doc) return EMPTY_CELL_BUTTON_OPTIONS;
    const document = doc.document();
    const widgets = widgetNodes(document);
    const label = (node: (typeof widgets)[number]) => node.name || node.widget?.layoutType || node.id;
    const of = (type: string) => widgets.filter((node) => node.widget?.layoutType === type);
    const datasets: Record<string, string> = {};
    widgets.forEach((node) => {
      const tile = tileFromNode(node);
      const dataset = tile ? tileDataset(tile) : '';
      if (dataset) datasets[node.id] = dataset;
    });
    const saved = readJson<Array<{ name?: string; config?: Record<string, unknown> }>>('dvm.selfService.savedDashboards', []);
    return {
      options: {},
      targetOptions: chartTargets(document, '', '').map((target) => ({ label: target.label || target.componentId, value: target.componentId })),
      widgetOptions: widgets
        .filter((node) => WIDGET_PALETTE_META[node.widget!.layoutType]?.group !== 'inputs')
        .map((node) => ({ label: label(node), value: node.id })),
      popupOptions: of('POPUP').map((node) => ({ label: label(node), value: node.id })),
      screenOptions: screensOf(document).map((screen) => ({ label: screen.name || screen.id, value: screen.id })),
      sliderOptions: of('ZONE_SLIDER').map((node) => ({ label: label(node), value: node.id })),
      approvalTableOptions: widgets
        .filter((node) => APPROVAL_TABLE_TYPES.has(node.widget!.layoutType))
        .map((node) => ({ label: `Table: ${label(node)}`, value: node.id })),
      widgetDatasets: datasets,
      valueSourceOptions: valueSourceOptionsOf(document.pages.flatMap((page) => page.nodes)),
      dashboardOptions: (Array.isArray(saved) ? saved : [])
        .map((dashboard) => ({ label: dashboard.name || String(dashboard.config?.['dashboardId'] ?? ''), value: String(dashboard.config?.['dashboardId'] ?? '') }))
        .filter((option) => !!option.value),
    };
  });

  // ── Popup screens (as a Button widget's: a frame designed on the canvas) ────

  private popupFrame() {
    const config = buttonConfigOf(this.config());
    if (!this.doc || !opensPopupScreen(config) || !config.popupScreenId) return null;
    return this.doc.nodes().find((node) => node.id === config.popupScreenId && node.type === 'FRAME') ?? null;
  }

  popupState(): 'none' | 'shown' | 'hidden' {
    const frame = this.popupFrame();
    return !frame ? 'none' : frame.visible ? 'shown' : 'hidden';
  }

  /** A blank square frame for this button's popup, under everything on the page, linked straight away. */
  createPopupScreen(): void {
    if (!this.doc) return;
    const bounds = this.doc.contentBounds();
    const frame = createScreenFrame(480, 480);
    frame.name = `${this.tableName() || 'Table'} · ${this.label() || 'Button'} popup`;
    frame.fluid = false;
    frame.popupScreen = true;
    frame.x = Math.max(0, roundGeometry(bounds.x));
    frame.y = Math.ceil(bounds.y + bounds.height + 80);
    frame.style = { ...frame.style, radius: 14 };
    this.configChange.emit({ popupSource: 'screen', popupScreenId: frame.id });
    this.doc.addNode(frame);
    this.canvasView?.fitTo(frame);
  }

  openPopupScreen(): void {
    const frame = this.popupFrame();
    if (!frame || !this.doc) return;
    if (!frame.visible) this.doc.updateNode(frame.id, { visible: true }, 'Show popup screen');
    this.doc.select(frame.id);
    this.canvasView?.fitTo(frame);
  }

  hidePopupScreen(): void {
    const frame = this.popupFrame();
    if (!frame || !this.doc) return;
    this.doc.updateNode(frame.id, { visible: false }, 'Hide popup screen');
    this.doc.clearSelection();
    this.canvasView?.fitToWidth();
  }
}
